import { useEffect, useState } from "react";
import { api, formatIDR, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { ProposalPdfDialog } from "../components/ProposalPdfDialog";
import { toast } from "sonner";
import { FileDown, Share2, Save, FileSignature, X, AlertTriangle } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { generatePackagePDF, generateInternalPDF, buildWhatsAppText } from "../lib/pdf";

const KINDS = [
    { key: "lab", label: "Laboratorium", testid: "mcu-tab-lab" },
    { key: "radiology", label: "Radiologi", testid: "mcu-tab-rad" },
    { key: "jasa", label: "Jasa Tindakan", testid: "mcu-tab-jasa" },
    { key: "obat", label: "Obat & BMHP", testid: "mcu-tab-obat" },
    { key: "fasilitas", label: "Fasilitas", testid: "mcu-tab-fasilitas" },
];

export default function MCUCalculator() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [exams, setExams] = useState({ lab: [], radiology: [], jasa: [], obat: [], fasilitas: [] });
    const [settings, setSettings] = useState(null);
    const [selected, setSelected] = useState({}); // id -> exam
    const [search, setSearch] = useState("");
    const [tab, setTab] = useState("lab");
    const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

    const [meta, setMeta] = useState({
        name: "",
        description: "",
        patient_target: "",
        qty: 1,
        discount_percent: 0,
        discount_type: "percent",
        discount_amount: 0,
    });

    useEffect(() => {
        Promise.all([
            api.get("/exams/lab"),
            api.get("/exams/radiology"),
            api.get("/exams/jasa"),
            api.get("/exams/obat"),
            api.get("/exams/fasilitas"),
            api.get("/settings"),
        ]).then(([lab, rad, jasa, obat, fasilitas, s]) => {
            setExams({
                lab: lab.data,
                radiology: rad.data,
                jasa: jasa.data,
                obat: obat.data,
                fasilitas: fasilitas.data,
            });
            setSettings(s.data);
        });
    }, []);

    useEffect(() => {
        if (!id) return;
        api.get(`/packages/${id}`).then((res) => {
            const p = res.data;
            setMeta({
                name: p.name,
                description: p.description || "",
                patient_target: p.patient_target || "",
                qty: p.qty || 1,
                discount_percent: p.discount_percent || 0,
                discount_type: p.discount_type || "percent",
                discount_amount: p.discount_amount || 0,
            });
            const sel = {};
            for (const it of p.items) sel[it.exam_id] = it;
            setSelected(sel);
        });
    }, [id]);

    function toggle(exam) {
        setSelected((prev) => {
            const next = { ...prev };
            if (next[exam.id]) delete next[exam.id];
            else
                next[exam.id] = {
                    exam_id: exam.id,
                    kind: exam.kind,
                    code: exam.code,
                    name: exam.name,
                    unit_cost: exam.unit_cost,
                    unit_cost_lanjutan: exam.unit_cost_lanjutan || 0,
                    jasa_medis: exam.jasa_medis || 0,
                    price: exam.price,
                    price_paket: exam.price_paket || 0,
                    discount_type: "percent",
                    discount_value: 0,
                };
            return next;
        });
    }

    function updateItem(examId, patch) {
        setSelected((prev) => ({ ...prev, [examId]: { ...prev[examId], ...patch } }));
    }

    function removeItem(examId) {
        setSelected((prev) => {
            const next = { ...prev };
            delete next[examId];
            return next;
        });
    }

    const items = Object.values(selected);
    // Harga efektif paket: Harga Jual Paket, jika belum diisi pakai Harga Jual RS
    const effPrice = (i) => {
        const pp = Number(i.price_paket) || 0;
        return pp > 0 ? pp : Number(i.price) || 0;
    };
    // Diskon per item (persen atau nominal), dibatasi 0..harga efektif
    const itemDiscount = (i) => {
        const base = effPrice(i);
        const v = Number(i.discount_value) || 0;
        const d = i.discount_type === "amount" ? v : (base * v) / 100;
        return Math.min(Math.max(d, 0), base);
    };
    const itemNet = (i) => effPrice(i) - itemDiscount(i);
    // Total Unit Cost = Unit Cost Dasar + Unit Cost Lanjutan + Jasa Medis
    const totalUnitCost = items.reduce(
        (s, i) =>
            s +
            (Number(i.unit_cost) || 0) +
            (Number(i.unit_cost_lanjutan) || 0) +
            (Number(i.jasa_medis) || 0),
        0
    );
    const totalPriceRS = items.reduce((s, i) => s + (Number(i.price) || 0), 0);
    const totalPriceGross = items.reduce((s, i) => s + effPrice(i), 0);
    const totalItemDiscount = items.reduce((s, i) => s + itemDiscount(i), 0);
    // Subtotal setelah diskon per item (basis diskon global)
    const totalPrice = totalPriceGross - totalItemDiscount;
    const discount = Number(meta.discount_percent) || 0;
    const isAmountDiscount = meta.discount_type === "amount";
    const discountValue = isAmountDiscount
        ? Number(meta.discount_amount) || 0
        : totalPrice * (discount / 100);
    const finalPrice = Math.max(totalPrice - discountValue, 0);
    const qty = Number(meta.qty) || 1;
    const grandTotal = finalPrice * qty;

    async function savePackage() {
        if (!meta.name.trim()) {
            toast.error("Nama paket wajib diisi");
            return;
        }
        if (items.length === 0) {
            toast.error("Pilih minimal 1 pemeriksaan");
            return;
        }
        const payload = {
            ...meta,
            qty: Number(meta.qty) || 1,
            items: items.map((i) => ({
                ...i,
                discount_type: i.discount_type || "percent",
                discount_value: Number(i.discount_value) || 0,
            })),
        };
        try {
            if (id) {
                await api.put(`/packages/${id}`, payload);
                toast.success("Paket diperbarui");
            } else {
                const res = await api.post("/packages", payload);
                toast.success("Paket tersimpan");
                navigate(`/mcu/${res.data.id}`, { replace: true });
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    function downloadPDF(exportOptions = {}) {
        const pkg = {
            ...meta,
            qty,
            items,
            total_unit_cost: totalUnitCost,
            total_price: totalPrice,
            total_price_gross: totalPriceGross,
            total_item_discount: totalItemDiscount,
            total_price_rs: totalPriceRS,
            final_price: finalPrice,
            grand_total: grandTotal,
        };
        generatePackagePDF(pkg, settings, exportOptions);
        toast.success("PDF proposal berhasil dibuat");
    }

    function downloadInternalPDF() {
        const pkg = {
            ...meta,
            qty,
            items,
            total_unit_cost: totalUnitCost,
            total_price: totalPrice,
            total_price_gross: totalPriceGross,
            total_item_discount: totalItemDiscount,
            total_price_rs: totalPriceRS,
            final_price: finalPrice,
            grand_total: grandTotal,
        };
        generateInternalPDF(pkg, settings);
        toast.success("PDF pengajuan internal berhasil dibuat");
    }

    function shareWhatsApp() {
        const pkg = {
            ...meta,
            qty,
            items,
            final_price: finalPrice,
            grand_total: grandTotal,
        };
        const text = buildWhatsAppText(pkg, settings);
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }

    const listForTab = exams[tab] || [];
    const filtered = listForTab.filter(
        (e) =>
            e.name.toLowerCase().includes(search.toLowerCase()) ||
            e.code.toLowerCase().includes(search.toLowerCase()) ||
            (e.category || "").toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="space-y-6" data-testid="mcu-page">
            <div>
                <div className="label-uppercase mb-2">Kalkulator Paket</div>
                <h1 className="font-heading text-4xl tracking-tight font-semibold">
                    Kalkulator Paket Pemeriksaan
                </h1>
                <p className="text-[#4A5E57] mt-2 max-w-3xl">
                    Pilih pemeriksaan laboratorium, radiologi, jasa tindakan, obat & BMHP,
                    dan fasilitas, atur diskon, lalu simpan atau bagikan sebagai proposal
                    paket pemeriksaan dengan kop surat RS.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="surface-card p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <Input
                                placeholder="Cari pemeriksaan..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                data-testid="mcu-search"
                                className="max-w-sm"
                            />
                        </div>
                        <Tabs value={tab} onValueChange={setTab}>
                            <TabsList className="bg-[#E9EFEA] flex-wrap h-auto">
                                {KINDS.map((k) => (
                                    <TabsTrigger key={k.key} value={k.key} data-testid={k.testid}>
                                        {k.label} ({exams[k.key]?.length || 0})
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                            {KINDS.map((k) => (
                                <TabsContent key={k.key} value={k.key} className="mt-4">
                                    <ExamList
                                        exams={filtered}
                                        selected={selected}
                                        toggle={toggle}
                                        testidPrefix={`mcu-${k.key}`}
                                    />
                                </TabsContent>
                            ))}
                        </Tabs>
                    </div>

                    <SelectedItemsPanel
                        items={items}
                        effPrice={effPrice}
                        itemDiscount={itemDiscount}
                        itemNet={itemNet}
                        updateItem={updateItem}
                        removeItem={removeItem}
                    />
                </div>

                <div className="lg:col-span-1 space-y-4">
                    <div className="surface-card p-6">
                        <h3 className="font-heading text-lg font-medium mb-4">Info Paket</h3>
                        <div className="space-y-3">
                            <div>
                                <Label>Nama Paket</Label>
                                <Input
                                    value={meta.name}
                                    data-testid="mcu-name"
                                    onChange={(e) => setMeta({ ...meta, name: e.target.value })}
                                    placeholder="Contoh: Paket MCU Eksekutif"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Target Pasien</Label>
                                    <Input
                                        value={meta.patient_target}
                                        onChange={(e) =>
                                            setMeta({ ...meta, patient_target: e.target.value })
                                        }
                                        placeholder="Contoh: Karyawan usia 30-45"
                                    />
                                </div>
                                <div>
                                    <Label>Qty (Jml Pasien)</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={meta.qty}
                                        data-testid="mcu-qty"
                                        onChange={(e) => setMeta({ ...meta, qty: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>Deskripsi</Label>
                                <Textarea
                                    value={meta.description}
                                    onChange={(e) => setMeta({ ...meta, description: e.target.value })}
                                    rows={2}
                                />
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label>Diskon</Label>
                                    <div className="inline-flex rounded-md border border-[#D1DCD8] overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setMeta({ ...meta, discount_type: "percent" })
                                            }
                                            data-testid="mcu-discount-type-percent"
                                            className={`px-3 py-1 text-xs font-medium transition-colors ${
                                                !isAmountDiscount
                                                    ? "bg-[#245B4B] text-white"
                                                    : "bg-white text-[#4A5E57] hover:bg-[#E9EFEA]"
                                            }`}
                                        >
                                            %
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setMeta({ ...meta, discount_type: "amount" })
                                            }
                                            data-testid="mcu-discount-type-amount"
                                            className={`px-3 py-1 text-xs font-medium transition-colors ${
                                                isAmountDiscount
                                                    ? "bg-[#245B4B] text-white"
                                                    : "bg-white text-[#4A5E57] hover:bg-[#E9EFEA]"
                                            }`}
                                        >
                                            Rp
                                        </button>
                                    </div>
                                </div>
                                <Input
                                    type="number"
                                    min="0"
                                    value={
                                        isAmountDiscount
                                            ? meta.discount_amount
                                            : meta.discount_percent
                                    }
                                    data-testid="mcu-discount"
                                    onChange={(e) =>
                                        setMeta(
                                            isAmountDiscount
                                                ? { ...meta, discount_amount: e.target.value }
                                                : { ...meta, discount_percent: e.target.value }
                                        )
                                    }
                                />
                                <div className="text-xs text-[#6A9C89] mt-1">
                                    {isAmountDiscount
                                        ? "Nominal potongan dalam Rupiah"
                                        : "Persentase potongan dari subtotal paket"}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="surface-card p-6">
                        <div className="label-uppercase mb-2">Ringkasan</div>
                        <div className="text-sm space-y-2 mb-4">
                            <div className="flex justify-between">
                                <span className="text-[#4A5E57]">Item Terpilih</span>
                                <span data-testid="mcu-item-count" className="font-medium">
                                    {items.length}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[#4A5E57]">Total Unit Cost</span>
                                <span className="font-medium" data-testid="mcu-total-uc">
                                    {formatIDR(totalUnitCost)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[#4A5E57]">Total Harga Jual RS</span>
                                <span className="font-medium" data-testid="mcu-total-rs">
                                    {formatIDR(totalPriceRS)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[#4A5E57]">Total Harga Jual Paket</span>
                                <span className="font-medium" data-testid="mcu-total-paket">
                                    {formatIDR(totalPriceGross)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[#4A5E57]">- Diskon Item</span>
                                <span className="font-medium" data-testid="mcu-item-discount-total">
                                    {formatIDR(totalItemDiscount)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[#4A5E57]">Subtotal stlh Diskon Item</span>
                                <span className="font-medium" data-testid="mcu-subtotal-net">
                                    {formatIDR(totalPrice)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[#4A5E57]">
                                    - Diskon {isAmountDiscount ? "(Rp)" : `${discount}%`}
                                </span>
                                <span className="font-medium">{formatIDR(discountValue)}</span>
                            </div>
                        </div>
                        <div className="p-4 rounded-md bg-[#E9EFEA] mb-3">
                            <div className="text-xs uppercase tracking-widest text-[#245B4B] mb-1">
                                Harga Paket Final
                            </div>
                            <div
                                className="font-heading text-3xl font-semibold text-[#1A362D]"
                                data-testid="mcu-final-price"
                            >
                                {formatIDR(finalPrice)}
                            </div>
                        </div>
                        <div className="p-4 rounded-md bg-[#245B4B] mb-4">
                            <div className="text-xs uppercase tracking-widest text-[#C4DAD2] mb-1">
                                Grand Total ({qty} × Harga Paket Final)
                            </div>
                            <div
                                className="font-heading text-2xl font-semibold text-white"
                                data-testid="mcu-grand-total"
                            >
                                {formatIDR(grandTotal)}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Button
                                onClick={savePackage}
                                data-testid="mcu-save-btn"
                                className="w-full bg-[#245B4B] hover:bg-[#1A4236] text-white"
                            >
                                <Save size={16} className="mr-2" />
                                {id ? "Perbarui Paket" : "Simpan Paket"}
                            </Button>
                            <div className="grid grid-cols-2 gap-2">
                                <Button
                                    onClick={() => setPdfDialogOpen(true)}
                                    variant="outline"
                                    data-testid="mcu-pdf-btn"
                                    className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                                >
                                    <FileDown size={16} className="mr-1" /> Proposal
                                </Button>
                                <Button
                                    onClick={downloadInternalPDF}
                                    variant="outline"
                                    data-testid="mcu-internal-pdf-btn"
                                    className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                                >
                                    <FileSignature size={16} className="mr-1" /> Pengajuan
                                </Button>
                            </div>
                            <ProposalPdfDialog
                                open={pdfDialogOpen}
                                onOpenChange={setPdfDialogOpen}
                                onExport={downloadPDF}
                            />
                            <Button
                                onClick={shareWhatsApp}
                                variant="outline"
                                data-testid="mcu-wa-btn"
                                className="w-full border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                            >
                                <Share2 size={16} className="mr-1" /> Bagikan via WhatsApp
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const KIND_LABELS = {
    lab: "Laboratorium",
    radiology: "Radiologi",
    jasa: "Jasa Tindakan",
    obat: "Obat & BMHP",
    fasilitas: "Fasilitas",
};

function SelectedItemsPanel({ items, effPrice, itemDiscount, itemNet, updateItem, removeItem }) {
    // Total Unit Cost per item = Unit Cost Dasar + Unit Cost Lanjutan + Jasa Medis
    const itemUC = (i) =>
        (Number(i.unit_cost) || 0) +
        (Number(i.unit_cost_lanjutan) || 0) +
        (Number(i.jasa_medis) || 0);
    const sumUC = items.reduce((s, i) => s + itemUC(i), 0);
    const sumGross = items.reduce((s, i) => s + effPrice(i), 0);
    const sumDisc = items.reduce((s, i) => s + itemDiscount(i), 0);
    const sumNet = items.reduce((s, i) => s + itemNet(i), 0);
    // Item rugi: harga net di bawah total unit cost
    const isLoss = (i) => itemNet(i) < itemUC(i);
    const lossCount = items.filter(isLoss).length;
    return (
        <div className="surface-card p-6" data-testid="mcu-selected-panel">
            <h3 className="font-heading text-lg font-medium mb-1">
                Item Terpilih ({items.length})
            </h3>
            <p className="text-xs text-[#6A9C89] mb-4">
                Atur diskon per item (persen atau nominal) dan hapus item langsung dari daftar ini.
                Kolom Total Unit Cost membantu memastikan diskon tidak menjual di bawah biaya.
            </p>
            {lossCount > 0 && (
                <div
                    className="flex items-center gap-2 mb-4 px-3 py-2 rounded-md bg-[#FBEAE7] border border-[#E8B4A8] text-[#B3432B] text-sm"
                    data-testid="mcu-loss-warning"
                >
                    <AlertTriangle size={16} className="shrink-0" />
                    <span>
                        <span className="font-semibold">{lossCount} item rugi:</span> harga net di
                        bawah total unit cost. Kurangi diskon pada item bertanda merah.
                    </span>
                </div>
            )}
            {items.length === 0 ? (
                <div
                    className="text-sm text-[#6A9C89] py-6 text-center"
                    data-testid="mcu-selected-empty"
                >
                    Belum ada item dipilih. Centang pemeriksaan pada daftar di atas.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-[#4A5E57] border-b border-[#D1DCD8]">
                                <th className="py-2 pr-2 font-medium">Item</th>
                                <th className="py-2 pr-2 font-medium text-right">Total Unit Cost</th>
                                <th className="py-2 pr-2 font-medium text-right">Harga Paket</th>
                                <th className="py-2 pr-2 font-medium">Diskon Item</th>
                                <th className="py-2 pr-2 font-medium text-right">Harga Net</th>
                                <th className="py-2 w-8"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E9EFEA]">
                            {items.map((i) => {
                                const isAmt = i.discount_type === "amount";
                                const disc = itemDiscount(i);
                                const loss = isLoss(i);
                                return (
                                    <tr
                                        key={i.exam_id}
                                        data-testid={`mcu-selected-row-${i.code}`}
                                        className={loss ? "bg-[#FBEAE7]" : ""}
                                    >
                                        <td className="py-2 pr-2">
                                            <div className="font-medium">{i.name}</div>
                                            <div className="text-xs text-[#6A9C89]">
                                                {i.code} • {KIND_LABELS[i.kind] || i.kind}
                                            </div>
                                        </td>
                                        <td
                                            className="py-2 pr-2 text-right whitespace-nowrap text-[#4A5E57]"
                                            data-testid={`mcu-item-uc-${i.code}`}
                                        >
                                            {formatIDR(itemUC(i))}
                                        </td>
                                        <td className="py-2 pr-2 text-right whitespace-nowrap">
                                            {formatIDR(effPrice(i))}
                                        </td>
                                        <td className="py-2 pr-2">
                                            <div className="flex items-center gap-1">
                                                <div className="inline-flex rounded-md border border-[#D1DCD8] overflow-hidden shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            updateItem(i.exam_id, {
                                                                discount_type: "percent",
                                                            })
                                                        }
                                                        data-testid={`mcu-item-disc-percent-${i.code}`}
                                                        className={`px-2 py-1 text-[10px] font-medium transition-colors ${
                                                            !isAmt
                                                                ? "bg-[#245B4B] text-white"
                                                                : "bg-white text-[#4A5E57] hover:bg-[#E9EFEA]"
                                                        }`}
                                                    >
                                                        %
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            updateItem(i.exam_id, {
                                                                discount_type: "amount",
                                                            })
                                                        }
                                                        data-testid={`mcu-item-disc-amount-${i.code}`}
                                                        className={`px-2 py-1 text-[10px] font-medium transition-colors ${
                                                            isAmt
                                                                ? "bg-[#245B4B] text-white"
                                                                : "bg-white text-[#4A5E57] hover:bg-[#E9EFEA]"
                                                        }`}
                                                    >
                                                        Rp
                                                    </button>
                                                </div>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={i.discount_value ?? 0}
                                                    data-testid={`mcu-item-disc-input-${i.code}`}
                                                    onChange={(e) =>
                                                        updateItem(i.exam_id, {
                                                            discount_value: e.target.value,
                                                        })
                                                    }
                                                    className="h-8 w-24 text-right"
                                                />
                                            </div>
                                            {disc > 0 && (
                                                <div className="text-[10px] text-[#6A9C89] mt-0.5">
                                                    Potongan: {formatIDR(disc)}
                                                </div>
                                            )}
                                        </td>
                                        <td
                                            className={`py-2 pr-2 text-right whitespace-nowrap font-medium ${
                                                loss ? "text-[#B3432B]" : ""
                                            }`}
                                            data-testid={`mcu-item-net-${i.code}`}
                                        >
                                            <span className="inline-flex items-center gap-1 justify-end">
                                                {loss && (
                                                    <AlertTriangle
                                                        size={13}
                                                        className="shrink-0"
                                                        data-testid={`mcu-item-loss-${i.code}`}
                                                    />
                                                )}
                                                {formatIDR(itemNet(i))}
                                            </span>
                                            {loss && (
                                                <div className="text-[10px] font-normal">
                                                    Di bawah unit cost
                                                </div>
                                            )}
                                        </td>
                                        <td className="py-2 text-right">
                                            <button
                                                type="button"
                                                onClick={() => removeItem(i.exam_id)}
                                                data-testid={`mcu-item-remove-${i.code}`}
                                                className="text-[#B3432B] hover:bg-[#F5E6E1] rounded p-1 transition-colors"
                                                title="Hapus item"
                                            >
                                                <X size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-[#D1DCD8] font-medium text-[#1A362D]">
                                <td className="py-2 pr-2">Total</td>
                                <td
                                    className="py-2 pr-2 text-right whitespace-nowrap"
                                    data-testid="mcu-selected-total-uc"
                                >
                                    {formatIDR(sumUC)}
                                </td>
                                <td
                                    className="py-2 pr-2 text-right whitespace-nowrap"
                                    data-testid="mcu-selected-total-gross"
                                >
                                    {formatIDR(sumGross)}
                                </td>
                                <td
                                    className="py-2 pr-2 text-[#B3432B] whitespace-nowrap"
                                    data-testid="mcu-selected-total-disc"
                                >
                                    - {formatIDR(sumDisc)}
                                </td>
                                <td
                                    className={`py-2 pr-2 text-right whitespace-nowrap ${
                                        sumNet < sumUC ? "text-[#B3432B]" : ""
                                    }`}
                                    data-testid="mcu-selected-total-net"
                                >
                                    {formatIDR(sumNet)}
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}

function ExamList({ exams, selected, toggle, testidPrefix }) {
    if (exams.length === 0) {
        return (
            <div className="text-center py-8 text-[#6A9C89]">
                Tidak ada pemeriksaan. Silakan tambahkan di master data.
            </div>
        );
    }
    return (
        <div className="divide-y divide-[#D1DCD8] max-h-[540px] overflow-y-auto">
            {exams.map((e) => {
                const active = !!selected[e.id];
                return (
                    <label
                        key={e.id}
                        className={`flex items-center gap-3 py-3 px-2 cursor-pointer hover:bg-[#F7F9F6] rounded-md ${
                            active ? "bg-[#E9EFEA]" : ""
                        }`}
                        data-testid={`${testidPrefix}-item-${e.code}`}
                    >
                        <Checkbox
                            checked={active}
                            onCheckedChange={() => toggle(e)}
                            data-testid={`${testidPrefix}-check-${e.code}`}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{e.name}</div>
                            <div className="text-xs text-[#6A9C89]">
                                {e.code} • {e.category || "Umum"}
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="text-xs text-[#6A9C89]">
                                RS <span className="font-medium text-[#4A5E57]">{formatIDR(e.price)}</span>
                            </div>
                            <div className="text-xs font-medium text-[#1A362D]">
                                Paket{" "}
                                {e.price_paket > 0 ? (
                                    formatIDR(e.price_paket)
                                ) : (
                                    <span className="text-[#6A9C89]">-</span>
                                )}
                            </div>
                            <div className="text-xs text-[#6A9C89]">
                                UC{" "}
                                {formatIDR(
                                    (e.unit_cost || 0) +
                                        (e.unit_cost_lanjutan || 0) +
                                        (e.jasa_medis || 0)
                                )}
                            </div>
                        </div>
                    </label>
                );
            })}
        </div>
    );
}
